'use client';


import Link from 'next/link';
import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    mobile: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const countryCodes = [
    { code: '+1', country: 'US/CA' },
    { code: '+44', country: 'UK' },
    { code: '+91', country: 'IN' },
    { code: '+86', country: 'CN' },
    { code: '+81', country: 'JP' },
    { code: '+49', country: 'DE' },
    { code: '+33', country: 'FR' },
    { code: '+39', country: 'IT' },
    { code: '+34', country: 'ES' },
    { code: '+55', country: 'BR' },
    { code: '+52', country: 'MX' },
    { code: '+7', country: 'RU' },
    { code: '+62', country: 'ID' },
    { code: '+92', country: 'PK' },
    { code: '+234', country: 'NG' },
    { code: '+880', country: 'BD' },
    { code: '+63', country: 'PH' },
    { code: '+84', country: 'VN' },
    { code: '+90', country: 'TR' },
    { code: '+66', country: 'TH' },
    { code: '+82', country: 'KR' },
    { code: '+65', country: 'SG' },
    { code: '+971', country: 'AE' },
    { code: '+47', country: 'NO' },
    { code: '+353', country: 'IE' },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Simulate form submission (in production, send to backend API)
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log('Form submitted:', formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        countryCode: '+1',
        mobile: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link href="/homepage" className="flex items-center gap-3 hover:opacity-80 transition-opacity w-fit">
            <AppImage
              src="/assets/images/intagg_logo-01-1770559550357.png"
              alt="Intaggs logo"
              width={40}
              height={40}
              className="rounded-md"
            />
            <div>
              <h1 className="font-heading font-bold text-xl text-foreground">Intaggs</h1>
              <p className="text-xs text-muted-foreground">news in 100 words</p>
            </div>
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="font-heading font-bold text-4xl text-foreground mb-4">Contact Us</h1>
          <p className="text-muted-foreground">
            Have a question or feedback? We'd love to hear from you. Fill out the form below and we'll get back to you as soon as possible.
          </p>
        </div>

        {/* Success Message */}
        {submitStatus === 'success' && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500 rounded-lg flex items-center gap-3">
            <Icon name="CheckCircleIcon" size={24} className="text-green-500" />
            <p className="text-green-500 font-medium">Thank you! Your message has been sent successfully.</p>
          </div>
        )}

        {/* Error Message */}
        {submitStatus === 'error' && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-3">
            <Icon name="ExclamationCircleIcon" size={24} className="text-red-500" />
            <p className="text-red-500 font-medium">Something went wrong. Please try again.</p>
          </div>
        )}

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="bg-card border border-border rounded-lg p-8">
          {/* Name Field */}
          <div className="mb-6">
            <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter your full name"
            />
          </div>

          {/* Email Field */}
          <div className="mb-6">
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="your.email@example.com"
            />
          </div>

          {/* Mobile Number with Country Code */}
          <div className="mb-6">
            <label htmlFor="mobile" className="block text-sm font-medium text-foreground mb-2">
              Mobile Number <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              <select
                name="countryCode"
                value={formData.countryCode}
                onChange={handleChange}
                className="px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {countryCodes.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.code} ({item.country})
                  </option>
                ))}
              </select>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                required
                pattern="[0-9]{6,15}"
                className="flex-1 px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="1234567890"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Enter 6-15 digits without spaces or dashes</p>
          </div>

          {/* Subject Field */}
          <div className="mb-6">
            <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-2">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="What is this regarding?"
            />
          </div>

          {/* Message Field */}
          <div className="mb-6">
            <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={6}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              placeholder="Tell us more about your inquiry..."
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-6 py-3 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></div>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Icon name="PaperAirplaneIcon" size={20} />
                <span>Send Message</span>
              </>
            )}
          </button>
        </form>

        {/* Alternative Contact */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground mb-2">Or reach us directly at:</p>
          <div className="flex flex-col gap-2">
            <a href="mailto:info@Intaggs.com" className="text-primary font-medium hover:underline">
              info@Intaggs.com
            </a>
            <a href="mailto:marketing@Intaggs.com" className="text-primary font-medium hover:underline">
              marketing@Intaggs.com
            </a>
            <a href="mailto:admin@Intaggs.com" className="text-primary font-medium hover:underline">
              admin@Intaggs.com
            </a>
            <a href="mailto:hr@Intaggs.com" className="text-primary font-medium hover:underline">
              hr@Intaggs.com
            </a>
          </div>
        </div>
      </div>
    </main>
  );
}